import React, { useState, useEffect, useCallback } from 'react';
import HiraPrivacyModal from './modal/HiraPrivacyModal';
import HiraIdentificationModal from './modal/HiraIdentificationModal';
import HiraThirdPartyModal from './modal/HiraThirdPartyModal';
import MechUpPrivacyModal from './modal/MechUpPrivacyModal';
import MechUpThirdPartyModal from './modal/MechUpThirdPartyModal';

const AuthInfoTemplate = ({ legalData, checkedItems, setCheckedItems, userInfo, setUserInfo, isNhis, invalidFields }) => {
  const [formData, setFormData] = useState({
    userNm: '',
    ssnFront: '',
    ssnBack: '',
    userTlno: '',
    birthDate: '',
  });
  const [maskedSsnBack, setMaskedSsnBack] = useState('');
  const [allChecked, setAllChecked] = useState(false);
  const [userInfoDelayedUpdate, setUserInfoDelayedUpdate] = useState(null);
  const [visibleModal, setVisibleModal] = useState(null); // 현재 활성화된 모달 관리
  const [showSsnBack, setShowSsnBack] = useState(false); // 주민등록번호 뒷자리 표시 여부

  useEffect(() => {
    if (userInfoDelayedUpdate) {
      if (Array.isArray(userInfoDelayedUpdate)) {
        setUserInfo((prevUserInfo) => {
          const updatedUserInfo = { ...prevUserInfo };
          userInfoDelayedUpdate.forEach(({ key, value }) => {
            updatedUserInfo[key] = value;
          });
          return updatedUserInfo;
        });
      } else {
        setUserInfo((prevUserInfo) => ({
          ...prevUserInfo,
          [userInfoDelayedUpdate.key]: userInfoDelayedUpdate.value,
        }));
      }
      setUserInfoDelayedUpdate(null);
    }
  }, [userInfoDelayedUpdate, setUserInfo]);

  /* 모두 체크됐을때 전체 체크 O */
  useEffect(() => {
    setAllChecked(checkedItems.length === legalData.length);
  }, [checkedItems, legalData]);

  useEffect(() => {
    if (userInfo) {
      setFormData((prevData) => ({
        ...prevData,
        userNm: userInfo.userNm || prevData.userNm,
        userTlno: formatPhoneNumber(userInfo.userTlno || prevData.userTlno),
        birthDate: userInfo.birthDate || prevData.birthDate,
        ssnFront: userInfo.ssnFront || prevData.ssnFront,
        ssnBack: userInfo.ssnBack || prevData.ssnBack,
      }));
      setMaskedSsnBack(maskSsnBack(userInfo.ssnBack || formData.ssnBack));
    }
  }, [userInfo]);

  const openModal = (legalSrno) => {
    setVisibleModal(legalSrno);
  };

  const closeModal = () => {
    setVisibleModal(null);
  };

  /* 주민등록번호 뒷자리 마스킹 처리 */
  const maskSsnBack = (ssn) => (ssn.length > 0 ? ssn[0] + '*'.repeat(ssn.length - 1) : ssn);

  const toggleShowSsnBack = () => setShowSsnBack((prev) => !prev);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'ssnBack' || name === 'ssnFront') {
      handleSsnChange(e, name);
    } else {
      setFormData((prevData) => ({ ...prevData, [name]: value }));
      setUserInfoDelayedUpdate({ key: name, value });
    }
  };

  /* 주민 등록번호 처리 로직 */
  const handleSsnChange = (e, name) => {
    const { value } = e.target;
    const numericValue = value.replace(/[^0-9*]/g, '');

    if (!/^[0-9*]*$/.test(value)) {
      return;
    }

    if (name === 'ssnFront') {
      if (numericValue.length === 6) {
        const yearPrefix = parseInt(numericValue.slice(0, 2), 10) <= 21 ? '20' : '19';
        setUserInfoDelayedUpdate([
          { key: 'ssnFront', value: numericValue },
          { key: 'userBod', value: yearPrefix + numericValue },
        ]);
      } else {
        setUserInfoDelayedUpdate({ key: 'ssnFront', value: numericValue });
      }
      setFormData((prevData) => ({ ...prevData, ssnFront: numericValue }));
    } else if (name === 'ssnBack') {
      if (e.nativeEvent.inputType === 'deleteContentBackward') {
        // 백스페이스 입력 시 마지막 문자를 제거
        const newSsnBack = formData.ssnBack.slice(0, -1);
        setFormData((prevData) => ({ ...prevData, ssnBack: newSsnBack }));
        setUserInfoDelayedUpdate({ key: 'ssnBack', value: newSsnBack });
        setMaskedSsnBack(maskSsnBack(newSsnBack));
      } else {
        // 숫자 추가 입력 시
        if (numericValue.length <= 7) {
          const newSsnBack = formData.ssnBack + numericValue.slice(-1);
          setFormData((prevData) => ({ ...prevData, ssnBack: newSsnBack }));
          setUserInfoDelayedUpdate({ key: 'ssnBack', value: newSsnBack });
          setMaskedSsnBack(maskSsnBack(numericValue));
        }
      }
    }
  };

  /* 핸드폰 번호 포멧 */
  const formatPhoneNumber = (phoneNumber) => {
    const cleaned = phoneNumber.replace(/\D/g, '').slice(0, 11);
    const match = cleaned.match(/^(\d{3})(\d{4})(\d{4})$/);
    return match ? `${match[1]}-${match[2]}-${match[3]}` : phoneNumber;
  };

  /* 핸드폰 번호 변경 이벤트 */
  const handlePhoneNumberChange = (e) => {
    let value = e.target.value.replace(/[^0-9-]/g, '');
    value = formatPhoneNumber(value);
    setFormData((prevData) => ({ ...prevData, userTlno: value }));
    setUserInfo((prevUserInfo) => ({ ...prevUserInfo, userTlno: value.replace(/-/g, '') }));
  };

  const handleCheckToggle = (legalSrno) => {
    setCheckedItems((prevItems) => (prevItems.includes(legalSrno) ? prevItems.filter((item) => item !== legalSrno) : [...prevItems, legalSrno]));
  };

  const handleAllCheckToggle = () => {
    if (allChecked) {
      setCheckedItems([]);
      setAllChecked(false);
    } else {
      setCheckedItems(legalData.map((item) => item.legalSrno));
      setAllChecked(true);
    }
  };

  /* 조건부 스타일을 위해 invalidFields에 걸린 input에 빨간색 border 추가 */
  const getInputClassName = (fieldName) => (invalidFields.includes(fieldName) ? 'bc-red' : '');

  return (
    <div className="info_wrap Padding-20 pt-80" style={{ marginBottom: 140 }}>
      <ul className="step_box">
        <li className="ing">
          <span className="ico_step"></span>
          <div>정보입력</div>
        </li>
        <li>
          <span className="ico_step"></span>
          <div>카카오인증</div>
        </li>
        <li>
          <span className="ico_step"></span>
          <div>인증완료</div>
        </li>
      </ul>
      <div className="mt-30">
        <h3 className="fs-18">이름</h3>
        <div className="mt-10">
          <input
            type="text"
            name="userNm"
            placeholder="이름을 입력해주세요"
            className={`Width-100 lh-50 h-50 ${getInputClassName('userNm')}`}
            value={formData.userNm}
            onChange={handleChange}
          />
        </div>
      </div>
      {isNhis ? (
        <div className="mt-20">
          <h3 className="fs-18">생년월일</h3>
          <div className="mt-10">
            <input
              type="tel"
              name="birthDate"
              placeholder="생년월일을 입력해주세요 (19701130)"
              className={`Width-100 lh-50 h-50 ${getInputClassName('birthDate')}`}
              maxLength={8}
              value={formData.birthDate}
              onChange={handleChange}
            />
          </div>
        </div>
      ) : (
        <div className="mt-20">
          <h3 className="fs-18">주민등록 번호</h3>
          <div className="flex jc_between mt-10" style={{ position: 'relative' }}>
            <input
              type="tel"
              name="ssnFront"
              placeholder="앞자리 입력"
              maxLength={6}
              className={`Width-50 lh-50 h-50 ${getInputClassName('ssnFront')}`}
              value={formData.ssnFront}
              onChange={handleChange}
            />
            {!showSsnBack && (
              <input
                type="tel"
                name="ssnBack"
                placeholder="뒷자리 입력"
                className={`Width-50 ml-10 lh-50 h-50 ${getInputClassName('ssnBack')}`}
                maxLength={7}
                value={maskedSsnBack}
                onChange={handleChange}
              />
            )}

            {/* 원본 뒷자리 입력 */}
            {showSsnBack && (
              <input
                type="tel"
                name="ssnBack"
                placeholder="뒷자리 입력"
                className={`Width-50 ml-10 lh-50 h-50 ${getInputClassName('ssnBack')}`}
                maxLength={7}
                value={formData.ssnBack}
                onChange={handleChange}
              />
            )}
            <img
              src={!showSsnBack ? '/assets/images/ico_eye_off.svg' : '/assets/images/ico_eye_on.svg'}
              alt="마스킹 토글"
              style={{
                width: 22,
                height: 18,
                position: 'absolute',
                right: 19,
                top: '50%',
                transform: 'translateY(-50%)',
                cursor: 'pointer',
              }}
              onClick={toggleShowSsnBack}
            />
          </div>
        </div>
      )}
      <div className="mt-20">
        <h3 className="fs-18">휴대폰 번호</h3>
        <div className="mt-10">
          <input
            type="tel"
            name="userTlno"
            placeholder="휴대폰 번호를 입력해주세요"
            className={`Width-100 lh-50 h-50 ${getInputClassName('userTlno')}`}
            maxLength={13}
            value={formData.userTlno}
            onChange={handlePhoneNumberChange}
          />
        </div>
      </div>
      <div className={`check_all mt-30 ${allChecked ? 'cheked' : ''}`} onClick={handleAllCheckToggle}>
        필수 약관 모두 동의
      </div>
      {legalData.map((item) => (
        <div
          key={item.legalSrno}
          className={`check_block none mt-10 ${checkedItems.includes(item.legalSrno) ? 'cheked' : ''}`}
          onClick={() => handleCheckToggle(item.legalSrno)}
        >
          {item.legalDesc}
          <span>필수</span>
          <button
            className="btn_pop"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              openModal(item.legalSrno);
            }}
          ></button>
        </div>
      ))}

      {/* 모달 조건부 렌더링 */}
      {isNhis ? (
        <>
          <MechUpPrivacyModal visible={visibleModal === '1'} onClose={closeModal} />
          <MechUpThirdPartyModal visible={visibleModal === '3'} onClose={closeModal} />
        </>
      ) : (
        <>
          <HiraPrivacyModal visible={visibleModal === '1'} onClose={closeModal} />
          <HiraIdentificationModal visible={visibleModal === '2'} onClose={closeModal} />
          <HiraThirdPartyModal visible={visibleModal === '3'} onClose={closeModal} />
        </>
      )}
    </div>
  );
};

export default AuthInfoTemplate;
